// Library Imports
import React, { Suspense, useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';

// Local Imports
import PrivateRoutes from '@/router/PrivateRoutes';
import PublicRoutes from '@/router/PublicRoutes';
import { RedirectHandler } from '@/router/RedirectHanlder';
import { RoleRouteGuard } from '@/router/guards/RoleRouteGuard';
import { useRoutePermissions } from '@/router/hooks/useRoutePermissions';
import { useEmailVerification } from '@/router/hooks/useEmailVerification';
import { creatorRoutes } from '@/router/routes/creatorRoutes';
import { brandRoutes } from '@/router/routes/brandRoutes';
import { adminRoutes } from '@/router/routes/adminRoutes';
import { publicRoutes } from '@/router/routes/publicRoutes';
import { commonRoutes } from '@/router/routes/commonRoutes';

const Router = () => {
    // Use custom hooks for route permissions and email verification
    const { userRole, canAccessRoute } = useRoutePermissions();
    useEmailVerification();

    const ROUTES_BY_ROLE = useMemo(
        () => ({
            creator: creatorRoutes,
            admin: adminRoutes,
            brand: brandRoutes,
        }),
        []
    );

    const privateRoutes = useMemo(
        () => ROUTES_BY_ROLE[userRole] ?? [],
        [userRole, ROUTES_BY_ROLE]
    );

    // Filter routes based on permissions
    const privateAllowedRoutes = useMemo(
        () => privateRoutes?.filter(route => canAccessRoute(route?.moduleName)),
        [privateRoutes, canAccessRoute]
    );

    return (
        <Suspense>
            <Routes>
                {/* Redirect based on authentication */}
                <Route path="*" element={<RedirectHandler />} />

                {/* Private Routes */}
                <Route element={<PrivateRoutes />}>
                    <Route element={<RoleRouteGuard />}>
                    {privateAllowedRoutes?.map((route, index) => (
                        <React.Fragment key={index}>
                            <Route path={route?.path} element={route?.element} />
                            {route?.child?.map((childRoute, childIndex) => (
                                <Route path={childRoute?.path} element={childRoute?.element} key={`${index}-${childIndex}`} />
                            ))}
                        </React.Fragment>
                    ))}
                    </Route>
                </Route>

                {/* Public Routes */}
                <Route element={<PublicRoutes />}>
                    {publicRoutes?.map((route, index) => (
                        <Route path={route?.path} element={route?.element} key={index} />
                    ))}
                </Route>

                {/* Common Routes */}
                {commonRoutes?.map((route, index) => (
                    <Route path={route?.path} element={route?.element} key={index} />
                ))}
            </Routes>
        </Suspense>
    );
};

export default Router;
